import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Hook for highlighting a table row based on URL query parameter.
 * Scrolls to the highlighted row and auto-clears after specified duration.
 *
 * @param duration - Time in ms before clearing highlight (default: 3000)
 * @returns highlightedRowRef to attach to highlighted row, highlightId from URL
 */
export const useHighlightRow = (duration = 3000) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedRowRef = useRef<HTMLTableRowElement>(null);
  const highlightId = searchParams.get("highlight");

  useEffect(() => {
    if (highlightId && highlightedRowRef.current) {
      highlightedRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      const timer = setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [highlightId, setSearchParams, duration]);

  return { highlightedRowRef, highlightId };
};
